import { useEffect, useCallback } from 'react';
import { useEvaluationStore } from '../../store/evaluationStore';
import { useNavigate } from 'react-router-dom';

interface EmbedMessage {
  type: string;
  payload?: any;
}

export const EmbedController = () => {
  const navigate = useNavigate();
  const store = useEvaluationStore();
  
  // 埋め込みモードかどうかを判定
  const isEmbedded = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('embedded') === 'true' || window.parent !== window;
  }, []);

  // 親ウィンドウへメッセージを送信
  const sendToParent = useCallback((type: string, payload?: any) => {
    if (window.parent !== window) {
      window.parent.postMessage({ type, payload }, '*');
    }
  }, []);

  // メッセージハンドラー
  const handleMessage = useCallback(async (event: MessageEvent<EmbedMessage>) => {
    // セキュリティチェック（本番環境では適切なオリジンチェックが必要）
    console.log('Embed received message:', event.data);

    const { type, payload } = event.data;

    switch (type) {
      case 'START_EVALUATION':
        if (payload?.evaluationId) {
          navigate(`/evaluation/${payload.evaluationId}`);
        }
        break;

      case 'LOAD_EVALUATION':
        if (payload?.evaluationId) {
          const success = await store.loadEvaluationById(payload.evaluationId);
          if (success) {
            sendToParent('EVALUATION_LOADED', {
              evaluationId: payload.evaluationId,
              data: {
                products: store.products,
                criteria: store.criteria,
                currentStep: store.currentStep
              }
            });
          } else {
            sendToParent('ERROR', {
              code: 'LOAD_ERROR',
              message: '評価データの読み込みに失敗しました'
            });
          }
        }
        break;

      case 'SAVE_EVALUATION':
        const saved = await store.saveDataAsync();
        if (saved) {
          sendToParent('EVALUATION_SAVED', {
            evaluationId: store.currentExaminationId,
            timestamp: new Date().toISOString()
          });
        } else {
          sendToParent('ERROR', {
            code: 'SAVE_ERROR',
            message: '保存に失敗しました'
          });
        }
        break;

      case 'SUBMIT_EVALUATION':
        // 評価を完了状態にする
        store.setCurrentStep(7); // 最終ステップ
        sendToParent('EVALUATION_COMPLETED', {
          evaluationId: store.currentExaminationId,
          results: {
            overallScore: 85, // 実際の計算が必要
            status: 'completed',
            completedAt: new Date().toISOString()
          }
        });
        break;

      case 'CHANGE_THEME':
        if (payload?.theme) {
          document.documentElement.setAttribute('data-theme', payload.theme);
        }
        break;

      case 'CHANGE_LANGUAGE':
        if (payload?.language) {
          // i18n言語変更（実装が必要）
          console.log('Language change to:', payload.language);
        }
        break;

      case 'SET_READONLY':
        if (typeof payload?.readonly === 'boolean') {
          store.setReadonly(payload.readonly);
        }
        break;

      default:
        console.warn('Unknown message type:', type);
    }
  }, [navigate, store, sendToParent]);

  // ステップ変更を監視
  useEffect(() => {
    if (isEmbedded()) {
      sendToParent('STEP_CHANGED', {
        currentStep: store.currentStep,
        totalSteps: 7
      });
    }
  }, [store.currentStep, isEmbedded, sendToParent]);

  // 初期化とクリーンアップ
  useEffect(() => {
    if (!isEmbedded()) {
      return;
    }

    // 埋め込みモードのスタイルを適用
    document.body.classList.add('embedded-mode');
    
    // メッセージリスナーを登録
    window.addEventListener('message', handleMessage);

    // 準備完了を通知
    sendToParent('READY', {
      version: '1.0.0',
      features: ['evaluation', 'rubric', 'report']
    });

    // クリーンアップ
    return () => {
      window.removeEventListener('message', handleMessage);
      document.body.classList.remove('embedded-mode');
    };
  }, [handleMessage, isEmbedded, sendToParent]);

  return null; // このコンポーネントは見た目を持たない
};